import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogTitle } from './ui/dialog';
import { createPortal } from 'react-dom';
import { 
  X, Search, Users, Network, ChevronRight, Check, 
  AlertCircle, Loader2, GraduationCap, ArrowLeft, Globe, Settings
} from 'lucide-react';
import { api } from '../services/api';

const ModalRattacherFamilleInterSite = ({ anneeScolaireId, onClose, onSuccess }) => {
  const [step, setStep] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [familles, setFamilles] = useState([]);
  const [selectedFamille, setSelectedFamille] = useState(null);
  const [elevesDisponibles, setElevesDisponibles] = useState([]);
  const [elevesSelectionnes, setElevesSelectionnes] = useState([]);
  const [eleveSearchTerm, setEleveSearchTerm] = useState('');
  const [rattachementLoading, setRattachementLoading] = useState(false);
  const [motifRattachement, setMotifRattachement] = useState('');
  const [error, setError] = useState('');
  const [reductionsParEleve, setReductionsParEleve] = useState({});

  useEffect(() => {
    if (step === 1) loadFamillesDisponibles();
  }, [step]);

  const loadFamillesDisponibles = async () => {
    setLoading(true);
    setError('');
    try {
      const result = await api.familles.getInterSiteDisponibles(searchTerm);
      if (result.success) setFamilles(result.data || []);
      else setError(result.error || 'Erreur lors de la recherche');
    } catch (err) {
      setError('Erreur de connexion au Hub inter-sites');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (step !== 1) return;
    const timer = setTimeout(() => loadFamillesDisponibles(), 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const loadElevesDisponibles = async (search = '') => {
    setLoading(true);
    try {
      const result = await api.familles.getElevesDisponibles(anneeScolaireId, search);
      if (result.success) setElevesDisponibles(result.data || []);
    } catch (err) {
      setError('Erreur chargement élèves disponibles');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (step !== 2) return;
    const timer = setTimeout(() => loadElevesDisponibles(eleveSearchTerm), 300);
    return () => clearTimeout(timer);
  }, [eleveSearchTerm]);

  const handleSelectFamille = async (famille) => {
    setSelectedFamille(famille);
    setStep(2);
    setError('');
    await loadElevesDisponibles('');
  };

  const toggleEleve = (eleveId) => {
    setElevesSelectionnes(prev =>
      prev.includes(eleveId) ? prev.filter(id => id !== eleveId) : [...prev, eleveId]
    );
  };

  const handlePasserAuxReductions = () => {
    if (elevesSelectionnes.length === 0) { setError('Sélectionnez au moins un élève'); return; }
    setError('');
    // Le défaut n'est plus « valeur famille » pour tout le monde. Un élève qui a
    // déjà une réduction propre la garde, sauf décision contraire explicite :
    // reprendre la valeur famille l'effacerait, ce qui peut le faire payer plus.
    const reductions = {};
    elevesSelectionnes.forEach(eleveId => {
      const eleve = elevesDisponibles.find(e => e.id === eleveId);
      const aUneReductionPropre = parseFloat(eleve?.reduction_custom_mensuel || 0) > 0
        || parseFloat(eleve?.reduction_custom_inscription || 0) > 0;

      reductions[eleveId] = {
        mode: aUneReductionPropre ? 'conserver' : 'famille',
        motif: '',
        reduction_inscription: parseFloat(selectedFamille?.reduction_inscription || 0),
        reduction_mensuel: parseFloat(selectedFamille?.reduction_mensuel || 0)
      };
    });
    setReductionsParEleve(reductions);
    setStep(3);
  };

  const handleChangeMode = (eleveId, mode) => {
    setReductionsParEleve(prev => ({
      ...prev,
      [eleveId]: {
        ...prev[eleveId],
        mode,
        ...(mode === 'famille' ? {
          reduction_inscription: parseFloat(selectedFamille?.reduction_inscription || 0),
          reduction_mensuel: parseFloat(selectedFamille?.reduction_mensuel || 0)
        } : {})
      }
    }));
  };

  const handleChangeReduction = (eleveId, field, value) => {
    setReductionsParEleve(prev => ({
      ...prev,
      [eleveId]: { ...prev[eleveId], [field]: parseFloat(value) || 0 }
    }));
  };

  // Le motif n'est pas un montant : le passer par handleChangeReduction le
  // transformerait en 0.
  const handleChangeReductionTexte = (eleveId, field, value) => {
    setReductionsParEleve(prev => ({
      ...prev,
      [eleveId]: { ...prev[eleveId], [field]: value }
    }));
  };

  const handleRattacher = async () => {
    if (elevesSelectionnes.length === 0) { setError('Sélectionnez au moins un élève'); return; }
    if (!motifRattachement.trim()) {
      setError('Le motif du rattachement est requis : il sera lu par le validateur.');
      return;
    }

    // Le serveur refuse une éviction sans motif ; le dire ici évite un aller-retour
    // qui laisserait l'utilisateur chercher quel élève pose problème.
    const sansMotif = elevesSelectionnes.filter((eleveId) => {
      const choix = reductionsParEleve[eleveId];
      const eleve = elevesDisponibles.find(e => e.id === eleveId);
      const aUneReductionPropre = parseFloat(eleve?.reduction_custom_mensuel || 0) > 0
        || parseFloat(eleve?.reduction_custom_inscription || 0) > 0;
      return aUneReductionPropre && choix?.mode === 'famille' && !String(choix?.motif || '').trim();
    });

    if (sansMotif.length > 0) {
      const noms = sansMotif
        .map((id) => {
          const eleve = elevesDisponibles.find(e => e.id === id);
          return eleve ? `${eleve.prenom} ${eleve.nom}` : `#${id}`;
        })
        .join(', ');
      setError(`Indiquez le motif du remplacement de la réduction propre pour : ${noms}.`);
      return;
    }
    setRattachementLoading(true);
    setError('');
    try {
      const result = await api.familles.rattacherInterSite(
        selectedFamille.famille_uuid,
        elevesSelectionnes,
        reductionsParEleve,
        motifRattachement.trim()
      );
      if (result.success) {
        onSuccess && onSuccess(result);
      } else {
        setError(result.error || 'Erreur lors du rattachement');
      }
    } catch (err) {
      setError('Erreur lors du rattachement');
    } finally {
      setRattachementLoading(false);
    }
  };

  const formatMontant = (montant) => {
    return new Intl.NumberFormat('fr-FR').format(Math.round(montant || 0)) + ' FCFA';
  };

  const getEleveDetails = (eleveId) => elevesDisponibles.find(e => e.id === eleveId);

  return (
    <Dialog open onOpenChange={(ouvert) => { if (!ouvert) onClose?.(); }}>
      {/* Parcours en trois étapes : la charte donne max-w-6xl. Le contenu
          étouffait dans max-w-2xl, listes d'élèves comprises. */}
      <DialogContent className="w-[95vw] max-w-6xl !max-h-[92vh] p-0 gap-0 overflow-hidden flex flex-col">
        <DialogTitle className="sr-only">Rattacher des élèves à une famille inter-sites</DialogTitle>
        
        {/* Header */}
        <div className="p-5 bg-gradient-to-r from-[#246990] to-[#319190] text-white flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-3">
            {step > 1 && (
              <button
                onClick={() => {
                  if (step === 3) setStep(2);
                  else { setStep(1); setSelectedFamille(null); setElevesSelectionnes([]); setReductionsParEleve({}); }
                }}
                className="p-1 hover:bg-white/20 rounded transition-colors"
              >
                <ArrowLeft size={20} />
              </button>
            )}
            <Network size={24} />
            <div>
              <h2 className="text-lg font-semibold">
                {step === 1 ? 'Lier à une famille inter-site' : step === 2 ? 'Rattacher des élèves' : 'Définir les réductions'}
              </h2>
              <p className="text-sm text-white/80">
                {step === 1 ? 'Recherchez une famille dans le réseau inter-sites' 
                  : step === 2 ? `Famille : ${selectedFamille?.famille_nom}`
                  : `${elevesSelectionnes.length} élève(s) sélectionné(s)`}
              </p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-white/20 rounded-lg transition-colors">
            <X size={20} />
          </button>
        </div>

        {/* Contenu */}
        <div className="flex-1 overflow-y-auto p-5">
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
              <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
              <span className="text-sm text-red-700">{error}</span>
            </div>
          )}

          {/* ÉTAPE 1 */}
          {step === 1 && (
            <div className="space-y-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input type="text" placeholder="Rechercher une famille par nom..." value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)} autoFocus
                  className="w-full pl-10 pr-4 py-3 border-2 border-gray-200 rounded-lg focus:border-[#246990] focus:ring-2 focus:ring-[#246990]/20 outline-none transition-all" />
              </div>
              <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg flex items-start gap-2">
                <Globe className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
                <span className="text-xs text-blue-700">
                  Les familles affichées sont celles activées en inter-site par les autres sites de votre groupe.
                </span>
              </div>
              {loading ? (
                <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 text-[#246990] animate-spin" /></div>
              ) : familles.length === 0 ? (
                <div className="text-center py-12">
                  <Network className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">Aucune famille inter-site trouvée</h3>
                  <p className="text-sm text-gray-500">{searchTerm ? 'Aucune famille ne correspond' : 'Aucune famille activée dans votre groupe'}</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {familles.map((famille) => (
                    <div key={famille.famille_uuid}
                      onClick={() => !famille.is_deja_rattachee && handleSelectFamille(famille)}
                      className={`p-4 rounded-lg border-2 transition-all ${
                        famille.is_deja_rattachee ? 'border-teal-200 bg-teal-50 cursor-default'
                          : 'border-gray-200 bg-white cursor-pointer hover:border-[#319190] hover:shadow-md'}`}>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className={`w-12 h-12 rounded-full flex items-center justify-center ${famille.is_deja_rattachee ? 'bg-teal-500' : 'bg-[#246990]'} text-white`}>
                            <Users size={20} />
                          </div>
                          <div>
                            <h3 className="font-semibold text-gray-900 text-base flex items-center gap-2">
                              {famille.famille_nom}
                              {famille.is_site_reference && <span className="text-[10px] bg-[#246990] text-white px-2 py-0.5 rounded-full font-semibold">VOTRE SITE</span>}
                              {famille.is_deja_rattachee && !famille.is_site_reference && (
                                <span className="text-[10px] bg-teal-600 text-white px-2 py-0.5 rounded-full font-semibold flex items-center gap-1"><Check size={10} />DÉJÀ LIÉE</span>
                              )}
                            </h3>
                            <div className="flex items-center gap-3 text-xs text-gray-500 mt-1">
                              <span>{famille.nb_membres_total} membre{famille.nb_membres_total > 1 ? 's' : ''}</span>
                              <span>•</span><span>Réf: {famille.site_reference}</span>
                            </div>
                          </div>
                        </div>
                        {!famille.is_deja_rattachee && <ChevronRight className="text-gray-400" size={20} />}
                      </div>
                      <div className="mt-3 pt-3 border-t border-gray-100 flex gap-4">
                        <div className="text-xs"><span className="text-gray-500">Réd. inscription : </span><span className="font-semibold text-emerald-700">{formatMontant(famille.reduction_inscription)}</span></div>
                        <div className="text-xs"><span className="text-gray-500">Réd. mensuelle : </span><span className="font-semibold text-emerald-700">{formatMontant(famille.reduction_mensuel)}</span></div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* ÉTAPE 2 */}
          {step === 2 && selectedFamille && (
            <div className="space-y-4">
              <div className="p-4 bg-[#eef7fb] rounded-lg border border-[#246990]/20">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-[#246990] text-white flex items-center justify-center"><Network size={18} /></div>
                  <div>
                    <h3 className="font-semibold text-[#246990]">{selectedFamille.famille_nom}</h3>
                    <p className="text-xs text-gray-600">{selectedFamille.nb_membres_total} membres • Réduction : {formatMontant(selectedFamille.reduction_mensuel)}/mois</p>
                  </div>
                </div>
              </div>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input type="text" placeholder="Rechercher un élève..." value={eleveSearchTerm}
                  onChange={(e) => setEleveSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 border-2 border-gray-200 rounded-lg focus:border-[#319190] focus:ring-2 focus:ring-[#319190]/20 outline-none transition-all" />
              </div>
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-2">
                <AlertCircle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                <span className="text-xs text-amber-700">
                  Seuls les élèves <strong>sans famille</strong> sont affichés. Sélectionnez les élèves, puis définissez la réduction pour chacun à l'étape suivante.
                </span>
              </div>
              {elevesSelectionnes.length > 0 && (
                <div className="p-3 bg-[#319190]/10 border border-[#319190]/30 rounded-lg flex items-center justify-between">
                  <span className="text-sm font-medium text-[#319190]">{elevesSelectionnes.length} élève{elevesSelectionnes.length > 1 ? 's' : ''} sélectionné{elevesSelectionnes.length > 1 ? 's' : ''}</span>
                  <button onClick={() => setElevesSelectionnes([])} className="text-xs text-[#319190] hover:underline">Tout désélectionner</button>
                </div>
              )}
              {loading ? (
                <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 text-[#319190] animate-spin" /></div>
              ) : elevesDisponibles.length === 0 ? (
                <div className="text-center py-8">
                  <GraduationCap className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                  <p className="text-gray-500 text-sm">{eleveSearchTerm ? 'Aucun élève trouvé' : 'Aucun élève sans famille'}</p>
                </div>
              ) : (
                <div className="space-y-2 max-h-[40vh] overflow-y-auto">
                  {elevesDisponibles.map((eleve) => {
                    const isSelected = elevesSelectionnes.includes(eleve.id);
                    return (
                      <div key={eleve.id} onClick={() => toggleEleve(eleve.id)}
                        className={`p-3 rounded-lg border-2 cursor-pointer transition-all flex items-center justify-between ${isSelected ? 'border-[#319190] bg-[#319190]/5' : 'border-gray-200 bg-white hover:border-gray-300'}`}>
                        <div className="flex items-center gap-3">
                          <div className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 ${isSelected ? 'border-[#319190] bg-[#319190]' : 'border-gray-300 bg-white'}`}>
                            {isSelected && <Check className="w-3 h-3 text-white" />}
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <span className="font-medium text-gray-900">{eleve.nom} {eleve.prenom}</span>
                              {eleve.matricule && <span className="text-xs text-gray-400">{eleve.matricule}</span>}
                            </div>
                            <div className="flex items-center gap-2 text-xs text-gray-500 mt-0.5">
                              <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded font-medium">{eleve.classe_nom || 'Sans classe'}</span>
                              <span>Frais : {formatMontant(eleve.frais_mensuel_normal)}/mois</span>
                            </div>
                          </div>
                        </div>
                        {isSelected && <div className="text-xs text-[#319190] font-medium">→ {formatMontant((eleve.frais_mensuel_normal || 0) - (selectedFamille.reduction_mensuel || 0))}/mois</div>}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* ÉTAPE 3 : Réductions par élève */}
          {step === 3 && selectedFamille && (
            <div className="space-y-4">
              <div className="p-4 bg-[#eef7fb] rounded-lg border border-[#246990]/20">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-[#246990] text-white flex items-center justify-center"><Network size={18} /></div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-[#246990]">{selectedFamille.famille_nom}</h3>
                    <p className="text-xs text-gray-600">
                      Réduction famille : {formatMontant(selectedFamille.reduction_inscription)} (inscription) • {formatMontant(selectedFamille.reduction_mensuel)} (mensuel)
                    </p>
                  </div>
                </div>
              </div>

              <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg flex items-start gap-2">
                <Settings className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
                <span className="text-xs text-blue-700">
                  Pour chaque élève, choisissez la règle principale : <strong>réduction famille de référence</strong> ou <strong>réduction personnalisée locale</strong>. La personnalisée remplace la réduction famille pour cet élève.
                </span>
              </div>

              <div className="space-y-4">
                {elevesSelectionnes.map((eleveId) => {
                  const eleve = getEleveDetails(eleveId);
                  const reduction = reductionsParEleve[eleveId];
                  if (!eleve || !reduction) return null;
                  const montantApres = (eleve.frais_mensuel_normal || 0) - reduction.reduction_mensuel;

                  return (
                    <div key={eleveId} className="bg-white rounded-lg border-2 border-gray-200 overflow-hidden">
                      {/* En-tête élève */}
                      <div className="p-4 bg-gray-50 border-b border-gray-200">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-full bg-[#319190] text-white flex items-center justify-center text-xs font-semibold">
                              {(eleve.prenom || '')[0]}{(eleve.nom || '')[0]}
                            </div>
                            <div>
                              <span className="font-semibold text-gray-900">{eleve.nom} {eleve.prenom}</span>
                              <div className="flex items-center gap-2 text-xs text-gray-500">
                                <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded font-medium">{eleve.classe_nom}</span>
                                <span>Frais : {formatMontant(eleve.frais_mensuel_normal)}/mois</span>
                              </div>
                            </div>
                          </div>
                          <div className="text-right">
                            <div className="text-xs text-gray-500">Après réduction :</div>
                            <div className="text-lg font-semibold text-[#319190]">{formatMontant(montantApres)}/mois</div>
                          </div>
                        </div>
                      </div>

                      <div className="p-4 space-y-3">
                        {/* Option 0 : ne rien défaire. Proposée en premier, et
                            retenue par défaut, quand l'élève a déjà une réduction
                            à lui : la valeur famille la remplacerait. */}
                        {(parseFloat(eleve?.reduction_custom_mensuel || 0) > 0
                          || parseFloat(eleve?.reduction_custom_inscription || 0) > 0) && (
                          <div onClick={() => handleChangeMode(eleveId, 'conserver')}
                            className={`p-3 rounded-lg border-2 cursor-pointer transition-all ${reduction.mode === 'conserver' ? 'border-[#246990] bg-[#eef7fb]' : 'border-gray-200 hover:border-gray-300'}`}>
                            <div className="flex items-center gap-3">
                              <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${reduction.mode === 'conserver' ? 'border-[#246990]' : 'border-gray-300'}`}>
                                {reduction.mode === 'conserver' && <div className="w-3 h-3 bg-[#246990] rounded-full"></div>}
                              </div>
                              <div className="flex-1">
                                <span className="font-medium text-sm">Conserver la réduction actuelle de cet élève</span>
                                <p className="text-xs text-gray-500 mt-1 ml-6">
                                  Il garde {formatMontant(eleve?.reduction_custom_mensuel)} par mois. La valeur famille ne s'applique pas à lui.
                                </p>
                              </div>
                            </div>
                          </div>
                        )}

                        {/* Option 1 : Même réduction */}
                        <div onClick={() => handleChangeMode(eleveId, 'famille')}
                          className={`p-3 rounded-lg border-2 cursor-pointer transition-all ${reduction.mode === 'famille' ? 'border-[#319190] bg-[#319190]/5' : 'border-gray-200 hover:border-gray-300'}`}>
                          <div className="flex items-center gap-3">
                            <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${reduction.mode === 'famille' ? 'border-[#319190]' : 'border-gray-300'}`}>
                              {reduction.mode === 'famille' && <div className="w-3 h-3 bg-[#319190] rounded-full"></div>}
                            </div>
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                
                                <span className="font-medium text-sm">Appliquer la réduction famille de référence</span>
                              </div>
                              <p className="text-xs text-gray-500 mt-1 ml-6">L'élève suit la réduction pilotée par le site de référence.</p>
                              {reduction.mode === 'famille' && (
                                <div className="flex gap-6 mt-2 ml-6">
                                  <div className="text-xs"><span className="text-gray-500">Inscription : </span><span className="font-semibold text-emerald-700">{formatMontant(selectedFamille.reduction_inscription)}</span></div>
                                  <div className="text-xs"><span className="text-gray-500">Mensuel : </span><span className="font-semibold text-emerald-700">{formatMontant(selectedFamille.reduction_mensuel)}</span></div>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>

                        {/* L'alerte compare deux montants absolus. « Il paiera X
                            de plus » se lirait comme une addition aux frais,
                            c'est-à-dire le contresens que l'alerte doit dissiper. */}
                        {reduction.mode === 'famille'
                          && parseFloat(eleve?.reduction_custom_mensuel || 0) > 0 && (
                          <div className="ml-8 rounded-lg border border-amber-200 bg-amber-50 p-3 space-y-2">
                            <p className="text-xs text-amber-900">
                              La valeur famille <span className="font-semibold">remplace</span> la réduction propre de cet élève,
                              elle ne s'y ajoute pas. Avec la sienne ({formatMontant(eleve.reduction_custom_mensuel)}), il paie{' '}
                              <span className="font-semibold">{formatMontant((eleve.frais_mensuel_normal || 0) - eleve.reduction_custom_mensuel)}</span> par mois ;
                              en reprenant la valeur famille ({formatMontant(selectedFamille?.reduction_mensuel)}), il paiera{' '}
                              <span className="font-semibold">{formatMontant((eleve.frais_mensuel_normal || 0) - (selectedFamille?.reduction_mensuel || 0))}</span>.
                            </p>
                            <label className="block space-y-1">
                              <span className="text-xs font-medium text-amber-900">
                                Motif de ce remplacement <span className="text-red-500">*</span>
                              </span>
                              <input
                                value={reduction.motif || ''}
                                onChange={(event) => handleChangeReductionTexte(eleveId, 'motif', event.target.value)}
                                onClick={(event) => event.stopPropagation()}
                                placeholder="Ex. Alignement décidé avec la famille"
                                className="w-full h-9 rounded-lg border border-amber-300 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#246990]/15 focus:border-[#246990]"
                              />
                            </label>
                          </div>
                        )}

                        {/* Option 2 : Personnalisé */}
                        <div onClick={() => handleChangeMode(eleveId, 'personnalise')}
                          className={`p-3 rounded-lg border-2 cursor-pointer transition-all ${reduction.mode === 'personnalise' ? 'border-orange-400 bg-orange-50' : 'border-gray-200 hover:border-gray-300'}`}>
                          <div className="flex items-center gap-3">
                            <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${reduction.mode === 'personnalise' ? 'border-orange-400' : 'border-gray-300'}`}>
                              {reduction.mode === 'personnalise' && <div className="w-3 h-3 bg-orange-400 rounded-full"></div>}
                            </div>
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                             
                                <span className="font-medium text-sm">Définir une réduction personnalisée locale</span>
                              </div>
                              <p className="text-xs text-gray-500 mt-1 ml-6">Remplace la réduction famille pour cet élève sur ce site, sans cumul automatique.</p>
                            </div>
                          </div>
                        </div>

                        {/* Champs personnalisés */}
                        {reduction.mode === 'personnalise' && (
                          <div className="ml-8 p-4 bg-orange-50 border border-orange-200 rounded-lg space-y-3">
                            <div>
                              <label className="block text-xs font-medium text-gray-700 mb-1">Réduction sur frais d'inscription</label>
                              <div className="relative">
                                <input type="number" min="0" value={reduction.reduction_inscription}
                                  onChange={(e) => handleChangeReduction(eleveId, 'reduction_inscription', e.target.value)}
                                  onClick={(e) => e.stopPropagation()}
                                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-400 focus:border-orange-400 outline-none text-sm pr-16" />
                                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-500 font-medium">FCFA</span>
                              </div>
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-gray-700 mb-1">Réduction sur frais mensuels</label>
                              <div className="relative">
                                <input type="number" min="0" value={reduction.reduction_mensuel}
                                  onChange={(e) => handleChangeReduction(eleveId, 'reduction_mensuel', e.target.value)}
                                  onClick={(e) => e.stopPropagation()}
                                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-400 focus:border-orange-400 outline-none text-sm pr-16" />
                                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-500 font-medium">FCFA</span>
                              </div>
                            </div>
                            <div className="p-2 bg-white rounded border border-orange-200 flex items-center justify-between">
                              <span className="text-xs text-gray-600">Montant mensuel après réduction :</span>
                              <span className="text-sm font-semibold text-orange-700">{formatMontant((eleve.frais_mensuel_normal || 0) - reduction.reduction_mensuel)}</span>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Le rattachement part désormais en demande : le validateur lira
                  ce motif sans avoir eu la conversation qui l'a précédé. */}
              <div className="mt-4 rounded-lg border border-gray-200 bg-white p-4">
                <label className="block space-y-1.5">
                  <span className="text-sm font-medium text-gray-700">
                    Motif du rattachement <span className="text-red-500">*</span>
                  </span>
                  <textarea
                    value={motifRattachement}
                    onChange={(event) => setMotifRattachement(event.target.value)}
                    placeholder="Ex. Fratrie déjà inscrite sur l'autre site, confirmée par le parent…"
                    rows={3}
                    className="w-full rounded-lg border border-gray-200 px-3 py-2.5 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-[#246990]/15 focus:border-[#246990]"
                  />
                  <span className="block text-[11px] text-gray-500">
                    Le rattachement change ce que ces élèves paient : il sera appliqué après validation
                    au Centre de Support.
                  </span>
                </label>
              </div>
            </div>
          )}
        </div>

        {/* Footer étape 2 */}
        {step === 2 && (
          <div className="p-4 border-t border-gray-200 flex items-center justify-between flex-shrink-0 bg-gray-50">
            <div className="text-sm text-gray-600">
              {elevesSelectionnes.length > 0 ? `${elevesSelectionnes.length} élève(s) sélectionné(s)` : 'Sélectionnez des élèves'}
            </div>
            <div className="flex gap-3">
              <button onClick={onClose} className="px-4 py-2.5 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors text-sm font-medium">Annuler</button>
              <button onClick={handlePasserAuxReductions} disabled={elevesSelectionnes.length === 0}
                className="px-6 py-2.5 bg-[#319190] text-white rounded-lg hover:bg-[#2a7d7c] transition-colors text-sm font-semibold flex items-center gap-2 disabled:bg-gray-300 disabled:cursor-not-allowed">
                Définir les réductions <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}

        {/* Footer étape 3 */}
        {step === 3 && (
          <div className="p-4 border-t border-gray-200 flex items-center justify-between flex-shrink-0 bg-gray-50">
            <div className="text-sm text-gray-600">{elevesSelectionnes.length} élève(s) à rattacher</div>
            <div className="flex gap-3">
              <button onClick={() => setStep(2)} className="px-4 py-2.5 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors text-sm font-medium">Retour</button>
              <button onClick={handleRattacher} disabled={rattachementLoading}
                className="px-6 py-2.5 bg-[#319190] text-white rounded-lg hover:bg-[#2a7d7c] transition-colors text-sm font-semibold flex items-center gap-2 disabled:bg-gray-300 disabled:cursor-not-allowed">
                {rattachementLoading ? (<><Loader2 className="w-4 h-4 animate-spin" />Rattachement...</>) : (<><Network className="w-4 h-4" />Rattacher à la famille</>)}
              </button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ModalRattacherFamilleInterSite;
